// src/runtime/KnowledgeEvolutionRuntime/evolver.ts
// ============================================
// Knowledge Evolver - 夜间知识整理器
//
// 职责：夜间自动整理候选知识
//   - Merge（合并相似）
//   - Remove Duplicate（去重）
//   - Cluster（聚类：按来源 + 标签归类到目标分类）
//   - Summarize（摘要）
//
// 依据白皮书第十五章：Knowledge Evolution Runtime（夜间自动整理）
//   ├── Merge（合并相似）
//   ├── Remove Duplicate（去重）
//   ├── Cluster（聚类）
//   └── Summarize（摘要）
// ============================================

import type { CandidateKnowledge } from './types';

export class KnowledgeEvolver {
  /**
   * 夜间整理：去重 → 合并相似 → 聚类分类 → 生成摘要
   */
  evolve(candidates: CandidateKnowledge[]): CandidateKnowledge[] {
    if (candidates.length <= 1) return candidates;

    // 1. 去重（同摘要前 30 字视为重复，保留评分高者）
    const deduped = this.removeDuplicates(candidates);

    // 2. 聚类：按标签归类，更新 targetCategory
    for (const c of deduped) {
      c.targetCategory = this.clusterCategory(c);
    }

    // 3. 合并相似（同来源 + 高标签重合度）
    const merged = this.mergeSimilar(deduped);

    return merged;
  }

  /** 去重 */
  removeDuplicates(candidates: CandidateKnowledge[]): CandidateKnowledge[] {
    const seen = new Map<string, CandidateKnowledge>();
    for (const c of [...candidates].sort((a, b) => b.score - a.score)) {
      const key = c.summary.slice(0, 30);
      if (!seen.has(key)) seen.set(key, c);
      else {
        // 保留更高评分，合并标签
        const existing = seen.get(key)!;
        existing.tags = Array.from(new Set([...existing.tags, ...c.tags]));
        existing.score = Math.max(existing.score, c.score);
        if (existing.riskLevel === 'low' && c.riskLevel !== 'low') {
          existing.riskLevel = c.riskLevel; // 风险取最高
        }
      }
    }
    return Array.from(seen.values());
  }

  /** 聚类分类：根据标签推断目标正式库分类 */
  clusterCategory(c: CandidateKnowledge): string {
    const tag = c.tags[0];
    const map: Record<string, string> = {
      客户: 'CRM',
      营销: 'Marketing',
      产品: 'Product',
      'AI/LLM': 'LLM',
      财务: 'Finance',
      管理: 'Management',
    };
    return map[tag] || 'General';
  }

  /** 合并相似：同来源且标签重合 ≥ 60% 的合并为一条 */
  mergeSimilar(candidates: CandidateKnowledge[]): CandidateKnowledge[] {
    const result: CandidateKnowledge[] = [];
    const used = new Set<string>();
    for (let i = 0; i < candidates.length; i++) {
      if (used.has(candidates[i].id)) continue;
      const base = candidates[i];
      for (let j = i + 1; j < candidates.length; j++) {
        const other = candidates[j];
        if (used.has(other.id)) continue;
        if (base.source !== other.source) continue;
        const overlap = this.tagOverlap(base.tags, other.tags);
        if (overlap >= 0.6) {
          // 合并：内容拼接 + 摘要取长 + 评分提升
          base.content = `${base.content}\n${other.content}`;
          if (other.summary.length > base.summary.length) base.summary = other.summary;
          base.tags = Array.from(new Set([...base.tags, ...other.tags]));
          base.score = Math.min(100, base.score + Math.round(other.score * 0.3));
          used.add(other.id);
        }
      }
      result.push(base);
    }
    return result;
  }

  /** 标签重合度 0-1 */
  private tagOverlap(tagsA: string[], tagsB: string[]): number {
    if (tagsA.length === 0 || tagsB.length === 0) return 0;
    const setB = new Set(tagsB);
    const hits = tagsA.filter((t) => setB.has(t)).length;
    return hits / Math.max(tagsA.length, tagsB.length);
  }
}

export default KnowledgeEvolver;
